import React, { useState, useEffect, useRef } from "react";
import { PopoverContent } from "./Popover";
import { FaCaretDown, FaCheck } from "react-icons/fa";

const SortDropdown = ({
  headers,
  activeTab,
  sortConfig,
  setSortConfig,
  setShowSortOptions,
  isOpen = false,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [tempSortKey, setTempSortKey] = useState(sortConfig.key);
  const [tempSortDirection, setTempSortDirection] = useState(
    sortConfig.direction === "asc" ? "Sort by ascending" : "Sort by descending"
  );
  const [searchKey, setSearchKey] = useState("");
  const [searchDirection, setSearchDirection] = useState("");
  const firstInputRef = useRef(null);

  useEffect(() => {
    setOpenDropdown(null);
    setTempSortKey(sortConfig.key);
    setTempSortDirection(
      sortConfig.direction === "asc"
        ? "Sort by ascending"
        : "Sort by descending"
    );
    setSearchKey("");
    setSearchDirection("");
  }, [isOpen]);

  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.blur();
    }

    setTempSortKey(sortConfig.key);
    setTempSortDirection(
      sortConfig.direction === "asc"
        ? "Sort by ascending"
        : "Sort by descending"
    );
  }, [sortConfig]);

  const applySort = () => {
    setSortConfig({
      key: tempSortKey,
      direction: tempSortDirection === "Sort by ascending" ? "asc" : "desc",
    });
    setShowSortOptions(false);
  };

  const resetSort = () => {
    setSortConfig({ key: null, direction: "asc" });
    setTempSortKey(null);
    setTempSortDirection("Sort by ascending");
    setShowSortOptions(false);
  };

  const toggleDropdown = (dropdown) => {
    setOpenDropdown((prev) => (prev === dropdown ? null : dropdown));
  };

  const handleKeyEnter = (type) => {
    if (type === "key") {
      const filteredHeaders = headers.filter((header) =>
        header.value.toLowerCase().includes(searchKey.toLowerCase())
      );
      if (filteredHeaders.length > 0) {
        const firstHeader = filteredHeaders[0];
        setTempSortKey(
          firstHeader.key.includes(".")
            ? firstHeader.key.split(".")[1]
            : firstHeader.key
        );
        setSearchKey("");
        setOpenDropdown(null);
      }
    } else if (type === "direction") {
      const filteredDirections = ["Sort by ascending", "Sort by descending"].filter(
        (dir) => dir.toLowerCase().includes(searchDirection.toLowerCase())
      );
      if (filteredDirections.length > 0) {
        setTempSortDirection(filteredDirections[0]);
        setSearchDirection("");
        setOpenDropdown(null);
      }
    }
  };

  useEffect(() => {
    setSearchKey("");
    setSearchDirection("");
    setOpenDropdown(null);
  }, [setShowSortOptions]);

  return (
    <PopoverContent
      className="w-[476px] h-[112px] bg-white border rounded-[4px] shadow-lg p-4"
      align="end"
      style={{
        top: "36px",
        right: 0,
        border: "1px solid #C2C6CA",
      }}
    >
      <div className="flex">
        {/* Sort Key Dropdown with search */}
        <div className="relative w-1/2">
          <div className="relative">
            <input
              ref={firstInputRef}
              type="text"
              value={
                openDropdown === "key"
                  ? searchKey
                  : headers.find((h) => {
                      const normalizedKey = h.key.includes(".")
                        ? h.key.split(".")[1]
                        : h.key;
                      return normalizedKey === tempSortKey;
                    })?.value || ""
              }
              onChange={(e) => setSearchKey(e.target.value)}
              onClick={() => toggleDropdown("key")}
              onKeyDown={(e) => {
                if (e.key === "Enter" && openDropdown === "key") {
                  handleKeyEnter("key");
                }
              }}
              className="w-[222px] h-[40px] p-2 border mb-1 focus-within:border-[#35424D] focus-within:outline-none rounded-l-md font-medium border-[#C3C3C3] text-[#35424D] text-[13px] cursor-pointer "
            />
            <FaCaretDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>

          {openDropdown === "key" && (
            <div className="absolute top-full left-0 w-full max-h-[260px] overflow-y-auto border border-[#C3C3C3] bg-white rounded-md shadow-md z-10 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-button]:hidden">
              {headers
                .filter((header) =>
                  header.value.toLowerCase().includes(searchKey.toLowerCase())
                )
                .map((header) => (
                  <div
                    key={header.key}
                    className="p-2 text-[13px] text-[#35424D] cursor-pointer flex items-center hover:bg-[#F5F6F5]"
                    onClick={() => {
                      setTempSortKey(
                        header.key.includes(".")
                          ? header.key.split(".")[1]
                          : header.key
                      );
                      setSearchKey("");
                      setOpenDropdown(null);
                    }}
                  >
                    <span className="w-4 mr-2 flex justify-center">
                      {(header.key.includes(".")
                        ? header.key.split(".")[1]
                        : header.key) === tempSortKey && (
                        <FaCheck className="text-gray-500" />
                      )}
                    </span>
                    {header.value}
                  </div>
                ))}
              {headers.filter((header) =>
                header.value.toLowerCase().includes(searchKey.toLowerCase())
              ).length === 0 && (
                <div className="p-2 text-[13px] text-gray-400">
                  No matching columns
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sort Direction Dropdown with search */}
        <div className="relative w-1/2">
          <div className="relative">
            <input
              type="text"
              value={
                openDropdown === "direction"
                  ? searchDirection
                  : tempSortDirection
              }
              onChange={(e) => setSearchDirection(e.target.value)}
              onClick={() => toggleDropdown("direction")}
              onKeyDown={(e) => {
                if (e.key === "Enter" && openDropdown === "direction") {
                  handleKeyEnter("direction");
                }
              }}
              autoFocus={false}
              className="w-[222px] h-[40px] p-2 border mb-1 focus-within:border-[#35424D] focus-within:outline-none rounded-r-md font-medium border-[#C3C3C3] text-[#35424D] text-[13px] cursor-pointer "
            />
            <FaCaretDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>

          {openDropdown === "direction" && (
            <div className="absolute top-full left-0 w-full max-h-[260px] overflow-y-auto border border-[#C3C3C3] bg-white rounded-md shadow-md z-10 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-button]:hidden">
              {["Sort by ascending", "Sort by descending"]
                .filter((dir) =>
                  dir.toLowerCase().includes(searchDirection.toLowerCase())
                )
                .map((direction) => (
                  <div
                    key={direction}
                    className="p-2 text-[13px] text-[#35424D] cursor-pointer flex items-center hover:bg-[#F5F6F5]"
                    onClick={() => {
                      setTempSortDirection(direction);
                      setSearchDirection("");
                      setOpenDropdown(null);
                    }}
                  >
                    <span className="w-4 mr-2 flex justify-center">
                      {tempSortDirection === direction && (
                        <FaCheck className="text-gray-500" />
                      )}
                    </span>
                    {direction}
                  </div>
                ))}
              {["Sort by Ascending", "Sort by Descending"].filter((dir) =>
                dir.toLowerCase().includes(searchDirection.toLowerCase())
              ).length === 0 && (
                <div className="p-2 text-[13px] text-gray-400">
                  No matching directions
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <hr className="border-t border-[#C2C6CA] mt-1" />

      <div className="flex justify-between items-center h-[48px]">
        <button
          className="text-[#1C40C9] h-[32px] text-[13px] font-semibold hover:underline"
          onClick={resetSort}
        >
          Reset sort
        </button>
        <button
          className="bg-[#1C40C9] w-[75px] h-[32px] text-white rounded text-[13px] hover:bg-[#1537A6]"
          onClick={applySort}
        >
          Apply sort
        </button>
      </div>
    </PopoverContent>
  );
};

export default SortDropdown;
